import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, MapPin, Lock, CheckCircle2, DollarSign } from "lucide-react";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

export default function Opportunities() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);

  const load = () => api.get("/opportunities").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    try {
      const r = await api.post(`/opportunities/${id}/accept`);
      toast({ title: "Opportunity accepted", description: `Pay rate unlocked: $${r.data.pay_rate}/hr. Continue to your Client Journey.` });
      load();
    } catch (err) {
      toast({ title: "Cannot accept", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  if (!data.eligible) {
    return (
      <Card className="mx-auto max-w-xl text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100"><Lock className="h-6 w-6 text-slate-400" /></div>
        <h2 className="mt-4 text-lg font-bold text-slate-900">Opportunity Center locked</h2>
        <p className="mt-2 text-sm text-slate-500">Become a Certified Customer Service Representative to view and accept client opportunities.</p>
        <Button onClick={() => navigate("/assessment")} className="btn-primary-gradient mt-6 border-0 text-white">Take the assessment</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-base font-bold text-slate-900">Opportunity Center</h2>
        <p className="text-sm text-slate-500">Pay rates are revealed only after you accept an opportunity.</p>
      </Card>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {data.items.map((o) => (
          <Card key={o.id} className={o.accepted ? "ring-2 ring-violet-300" : ""}>
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100"><Briefcase className="h-5 w-5 text-violet-600" /></div>
              <div className="flex-1">
                <div className="font-bold text-slate-900">{o.role_title}</div>
                <div className="text-sm text-slate-500">{o.client_name}</div>
              </div>
              {o.accepted && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Accepted</span>}
            </div>
            <p className="mt-3 text-sm text-slate-600">{o.description}</p>
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {o.location}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {o.requirements.map((r) => (<span key={r} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600">{r}</span>))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                <DollarSign className="h-4 w-4 text-slate-400" />
                {o.accepted && o.pay_rate != null ? `$${o.pay_rate}/hr` : <span className="text-slate-400">Pay revealed after accepting</span>}
              </div>
              {o.accepted ? (
                <Button size="sm" onClick={() => navigate("/client-journey")} className="btn-primary-gradient border-0 text-white">Continue journey</Button>
              ) : (
                <Button size="sm" disabled={!!data.accepted_id} onClick={() => accept(o.id)} className="btn-primary-gradient border-0 text-white">Accept</Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
